import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { budgetsApi } from '../services/api';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from './ui/tabs';
import { toast } from 'sonner';
import { 
  MessageSquare, 
  Send, 
  ChevronDown, 
  ChevronRight,
  Edit2,
  Trash2,
  FileText,
  TrendingUp,
  Calendar
} from 'lucide-react';

const MONTHS = [
  { key: 'jul', label: 'July' },
  { key: 'aug', label: 'August' },
  { key: 'sep', label: 'September' },
  { key: 'oct', label: 'October' },
  { key: 'nov', label: 'November' },
  { key: 'dec', label: 'December' },
  { key: 'jan', label: 'January' },
  { key: 'feb', label: 'February' },
  { key: 'mar', label: 'March' },
  { key: 'apr', label: 'April' },
  { key: 'may', label: 'May' },
  { key: 'jun', label: 'June' },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-ZA', { 
    day: 'numeric', 
    month: 'short', 
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getMonthLabel = (monthKey) => {
  if (!monthKey) return 'Overall';
  const month = MONTHS.find(m => m.key === monthKey);
  return month ? month.label : monthKey;
};

const CommentsSidePanel = ({ 
  isOpen, 
  onClose, 
  budgetId, 
  accountCode, 
  accountName,
  showVarianceComments = false  // true for Budget vs Actuals page
}) => {
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentType, setCommentType] = useState('budget');
  const [selectedMonth, setSelectedMonth] = useState('overall');  // 'overall' or month key
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  
  // View filter - which month's comments to show
  const [viewFilter, setViewFilter] = useState('all');  // 'all', 'overall', or month key
  
  // Collapsible states
  const [budgetCommentsOpen, setBudgetCommentsOpen] = useState(true);
  const [varianceCommentsOpen, setVarianceCommentsOpen] = useState(true);
  
  const { isDark } = useTheme();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen && budgetId && accountCode) {
      loadComments();
    }
  }, [isOpen, budgetId, accountCode]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const res = await budgetsApi.getComments(budgetId, { account_code: accountCode });
      setComments(res.data);
    } catch (error) {
      toast.error('Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    
    setSubmitting(true);
    try {
      const res = await budgetsApi.addComment(budgetId, {
        account_code: accountCode,
        text: newComment.trim(),
        comment_type: commentType,
        month: selectedMonth === 'overall' ? null : selectedMonth
      });
      setComments([res.data, ...comments]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (commentId) => {
    if (!editText.trim()) return;
    
    try {
      const res = await budgetsApi.updateComment(budgetId, commentId, { text: editText.trim() });
      setComments(comments.map(c => c.id === commentId ? res.data : c));
      setEditingId(null);
      setEditText('');
      toast.success('Comment updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update comment');
    }
  };

  const handleDelete = async (commentId) => {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      await budgetsApi.deleteComment(budgetId, commentId);
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete comment');
    }
  };

  const startEdit = (comment) => {
    setEditingId(comment.id);
    setEditText(comment.text);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  // Filter comments based on view filter and type
  const filterComments = (commentsList, type) => {
    let filtered = commentsList.filter(c => c.comment_type === type);
    
    if (viewFilter !== 'all') {
      if (viewFilter === 'overall') {
        filtered = filtered.filter(c => !c.month);
      } else {
        filtered = filtered.filter(c => c.month === viewFilter);
      }
    }
    
    return filtered;
  };

  // Group comments by month for display
  const groupCommentsByMonth = (commentsList) => {
    const groups = { overall: [] };
    MONTHS.forEach(m => groups[m.key] = []);
    
    commentsList.forEach(comment => {
      if (comment.month) {
        groups[comment.month]?.push(comment);
      } else {
        groups.overall.push(comment);
      }
    });
    
    return groups;
  };

  // Separate and filter comments
  const budgetComments = filterComments(comments, 'budget');
  const varianceComments = filterComments(comments, 'variance');

  // Count comments per month for badges
  const getMonthCommentCount = (monthKey, type) => {
    return comments.filter(c => 
      c.comment_type === type && 
      (monthKey === 'overall' ? !c.month : c.month === monthKey)
    ).length;
  };

  const getTotalByType = (type) => comments.filter(c => c.comment_type === type).length;

  const CommentItem = ({ comment }) => {
    const isOwner = user?.id === comment.author_id;
    const isAdmin = user?.role === 'admin';
    const isEditing = editingId === comment.id;
    const wasEdited = comment.updated_at !== comment.created_at;

    return (
      <div className={`p-3 rounded-sm border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-white border-slate-200'}`}>
        {isEditing ? (
          <div className="space-y-2">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="min-h-[80px] rounded-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEdit} className="rounded-sm">
                Cancel
              </Button>
              <Button size="sm" onClick={() => handleEdit(comment.id)} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white">
                Save
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${isDark ? 'bg-zinc-700 text-zinc-300' : 'bg-slate-100 text-slate-600'}`}>
                  {comment.author_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {comment.author_name}
                  </p>
                  <div className="flex items-center gap-2">
                    <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                      {formatDate(comment.created_at)}
                      {wasEdited && <span className="ml-1">(edited)</span>}
                    </p>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                      <Calendar className="w-2.5 h-2.5 mr-1" />
                      {getMonthLabel(comment.month)}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {isOwner && (
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(comment)}>
                    <Edit2 className="w-3 h-3" />
                  </Button>
                )}
                {isAdmin && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => handleDelete(comment.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            <p className={`mt-2 text-sm whitespace-pre-wrap ${isDark ? 'text-zinc-300' : 'text-slate-600'}`}>
              {comment.text}
            </p>
          </>
        )}
      </div>
    );
  };

  const CommentsList = ({ commentsList, type, emptyMessage }) => {
    if (commentsList.length === 0) {
      return (
        <p className={`text-sm text-center py-4 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
          {emptyMessage}
        </p>
      );
    }
    return (
      <div className="space-y-2">
        {commentsList.map(comment => (
          <CommentItem key={comment.id} comment={comment} />
        ))}
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className={`w-[420px] sm:w-[480px] ${isDark ? 'bg-zinc-900 border-zinc-800' : ''}`}>
        <SheetHeader className="pb-4 border-b border-slate-200 dark:border-zinc-800">
          <SheetTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
            <MessageSquare className="w-5 h-5 text-emerald-600" />
            Comments
          </SheetTitle>
          <SheetDescription className={isDark ? 'text-zinc-400' : ''}>
            <span className="font-medium">{accountCode}</span> - {accountName}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100vh-180px)] mt-4">
          {/* Add Comment Section */}
          <div className={`p-3 rounded-sm border ${isDark ? 'bg-zinc-800/50 border-zinc-700' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                Type:
              </span>
              <div className="flex gap-1">
                <Button
                  variant={commentType === 'budget' ? 'default' : 'outline'}
                  size="sm"
                  className={`rounded-sm h-7 text-xs ${commentType === 'budget' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  onClick={() => setCommentType('budget')}
                >
                  <FileText className="w-3 h-3 mr-1" />
                  Budget
                </Button>
                {showVarianceComments && (
                  <Button
                    variant={commentType === 'variance' ? 'default' : 'outline'}
                    size="sm"
                    className={`rounded-sm h-7 text-xs ${commentType === 'variance' ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                    onClick={() => setCommentType('variance')}
                  >
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Variance
                  </Button>
                )}
              </div>
              
              <span className={`text-xs font-medium ml-2 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                Month:
              </span>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className={`w-[110px] h-7 text-xs rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-600' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="overall">Overall</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={`Add a ${commentType} comment for ${selectedMonth === 'overall' ? 'overall' : getMonthLabel(selectedMonth)}...`}
              className="min-h-[70px] rounded-sm resize-none text-sm"
            />
            <div className="flex justify-end mt-2">
              <Button 
                onClick={handleSubmit} 
                disabled={submitting || !newComment.trim()}
                className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                size="sm"
              >
                <Send className="w-4 h-4 mr-1" />
                {submitting ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </div>

          {/* Filter by Month */}
          <div className="mt-3 mb-2">
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                View:
              </span>
              <Select value={viewFilter} onValueChange={setViewFilter}>
                <SelectTrigger className={`w-[130px] h-7 text-xs rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  <SelectItem value="overall">Overall Only</SelectItem>
                  {MONTHS.map(m => (
                    <SelectItem key={m.key} value={m.key}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                ({budgetComments.length + varianceComments.length} comments)
              </span>
            </div>
          </div>

          {/* Comments List */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-24 rounded-sm" />
                <Skeleton className="h-24 rounded-sm" />
              </div>
            ) : comments.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No comments yet</p>
                <p className="text-sm">Be the first to add a comment</p>
              </div>
            ) : (
              <>
                {/* Budget Comments Section */}
                <Collapsible open={budgetCommentsOpen} onOpenChange={setBudgetCommentsOpen}>
                  <CollapsibleTrigger className={`w-full flex items-center justify-between p-2 rounded-sm ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100'}`}>
                    <div className="flex items-center gap-2">
                      {budgetCommentsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <FileText className="w-4 h-4 text-blue-500" />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        Budget Comments
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {budgetComments.length}
                      </Badge>
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <CommentsList 
                      commentsList={budgetComments} 
                      type="budget" 
                      emptyMessage={viewFilter === 'all' ? 'No budget comments' : `No budget comments for ${viewFilter === 'overall' ? 'overall' : getMonthLabel(viewFilter)}`}
                    />
                  </CollapsibleContent>
                </Collapsible>

                {/* Variance Comments Section - Only show if enabled */}
                {showVarianceComments && (
                  <Collapsible open={varianceCommentsOpen} onOpenChange={setVarianceCommentsOpen}>
                    <CollapsibleTrigger className={`w-full flex items-center justify-between p-2 rounded-sm ${isDark ? 'hover:bg-zinc-800' : 'hover:bg-slate-100'}`}>
                      <div className="flex items-center gap-2">
                        {varianceCommentsOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        <TrendingUp className="w-4 h-4 text-amber-500" />
                        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                          Variance Comments
                        </span>
                        <Badge variant="outline" className="text-xs">
                          {varianceComments.length}
                        </Badge>
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2">
                      <CommentsList 
                        commentsList={varianceComments} 
                        type="variance" 
                        emptyMessage={viewFilter === 'all' ? 'No variance comments' : `No variance comments for ${viewFilter === 'overall' ? 'overall' : getMonthLabel(viewFilter)}`}
                      />
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CommentsSidePanel;
